import { currencyValueFormatter } from '@/utils/formatters.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { isEqual } from 'lodash'
import {
  FillStrategies,
  HorizonOptions,
  ConvictionOptions,
} from './action-idea-form.ts'

export interface TradeReasonCategory {
  value: string
  title: string
  children?: { value: string; title: string }[]
}

export function getDecimalPlaces(value: number | undefined): number {
  if (value == null || !Number.isFinite(value) || Number.isInteger(value)) {
    return 0
  }
  const str = value.toString()
  if (str.includes('e-')) {
    return parseInt(str.split('e-')[1], 10)
  }
  return str.split('.')[1]?.length ?? 0
}

export function formatCurrency(
  value: number | undefined,
  currency = 'USD'
): string {
  return (
    currencyValueFormatter({ value } as ValueFormatterParams, {
      decimal: getDecimalPlaces(value),
      currency,
    }) ?? '-'
  )
}

export function translateFillStrategy(
  value: string | undefined,
  t: (key: string) => string
): string {
  const found = FillStrategies.find((fs) => fs.value === value)
  return found ? t(found.labelKey) : (value ?? '-')
}

export function translateHorizon(
  value: string | undefined,
  t: (key: string) => string
): string {
  const found = HorizonOptions.find((h) => h.value === value)
  return found ? t(found.labelKey) : (value ?? '-')
}

export function translateConviction(
  value: string | undefined,
  t: (key: string) => string
): string {
  const found = ConvictionOptions.find((c) => c.value === value)
  return found ? t(found.labelKey) : (value ?? '-')
}

export function translateTradeReason(
  value: string,
  tradeReasonsTreeData: TradeReasonCategory[]
): string {
  for (const category of tradeReasonsTreeData) {
    if (category.value === value) return category.title
    for (const child of category.children ?? []) {
      if (child.value === value) return child.title
    }
  }
  return value
}

export function resolveInitialFillStrategy(
  fillStrategy: string | undefined
): string | undefined {
  return fillStrategy === '15min-VWAP' || fillStrategy === '1hour-VWAP'
    ? FillStrategies[0].value
    : fillStrategy
}

export function parseTradeReasons(
  value: string | string[] | undefined
): string[] {
  const list = Array.isArray(value)
    ? value
    : (value ?? '').split(',').map((r) => r.trim())

  return list
    .map((r) => r.trim())
    .filter(Boolean)
    .slice()
    .sort()
}

export function haveTradeReasonsChanged(
  newValue: string | string[] | undefined,
  oldValue: string | undefined
): boolean {
  return !isEqual(parseTradeReasons(newValue), parseTradeReasons(oldValue))
}
